import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { fireHaptic } from '../hooks/useHaptics';
import { trackEvent } from '../utils/analytics';

// Lazy-load the (large) AIChat only the first time the panel is opened
// so the FAB itself costs almost nothing on initial paint.
const AIChat = lazy(() => import('./AIChat'));

/**
 * FloatingAIWidget — global concierge access.
 *
 * A soft-gold Sparkles FAB that lives in the bottom-right on every route.
 * Clicking it slides in a chat panel from the right (480 px on desktop,
 * full-screen on mobile) which mounts the existing <AIChat /> component.
 *
 *   ── Design notes ──────────────────────────────────────────────────
 *   • Midnight-blue circular button, gold hairline ring, `Sparkles` icon
 *     tinted gold. Soft breathing pulse via `animate-fade-in` on hover.
 *   • Sits above the mobile bottom nav (bottom-24 md:bottom-6) with a
 *     safe-area-inset fallback for iPhones.
 *   • Panel: `rounded-l-3xl` on desktop, edge-to-edge on < md, backdrop
 *     dismiss + ESC key + close-X in the top-right of the sheet.
 *   • Reuses AIChat's own persistence — dropping the panel and reopening
 *     it (or refreshing) restores the exact conversation.
 *
 *   The FAB is deliberately allowed to coexist with the inline AIChat on
 *   the homepage. Both share the same `useChatPersistence` store so state
 *   stays in sync; users just get quick access wherever they are.
 */
export default function FloatingAIWidget({ onNavigate, activeSection }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  // Once true, the AIChat chunk stays mounted so re-opens are instant.
  const [everOpened, setEverOpened] = useState(false);
  const panelRef = useRef(null);

  const closePanel = () => {
    setOpen(false);
    fireHaptic('tick');
    trackEvent('floating_chat_close');
  };

  const openPanel = () => {
    setOpen(true);
    setEverOpened(true);
    fireHaptic('nudge');
    trackEvent('floating_chat_open');
  };

  // ESC dismisses
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the sheet is up
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // When the chat requests a navigation (e.g. "Search flights" chip), close
  // the panel first so the user actually sees the destination page.
  const handleChatNavigate = (section) => {
    setOpen(false);
    if (onNavigate) onNavigate(section);
  };

  return (
    <>
      {/* ── FAB ─────────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={openPanel}
        aria-label={t('chat.floatingAria') || 'Open AI concierge'}
        data-testid="floating-ai-widget-button"
        className={`
          fixed z-[60] right-4 md:right-6
          bottom-24 md:bottom-6
          w-14 h-14 md:w-16 md:h-16
          rounded-full
          bg-midnight-900 text-gold-300
          border border-gold-400/60
          shadow-[0_10px_30px_rgba(15,23,42,0.35)]
          hover:shadow-[0_14px_38px_rgba(15,23,42,0.45)]
          hover:-translate-y-0.5 active:translate-y-0
          transition-all duration-300
          flex items-center justify-center
          group
        `}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      >
        {/* Gold ring pulse */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-gold-400/40 group-hover:ring-gold-400/70 transition-all"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -inset-1 rounded-full ring-1 ring-gold-400/20 animate-pulse"
        />
        <Sparkles className="w-6 h-6 md:w-7 md:h-7 relative z-10" strokeWidth={1.5} />
      </button>

      {/* ── Backdrop ────────────────────────────────────────────────── */}
      {open && (
        <div
          onClick={closePanel}
          aria-hidden
          data-testid="floating-ai-widget-backdrop"
          className="fixed inset-0 z-[70] bg-midnight-900/60 backdrop-blur-sm animate-fade-in"
        />
      )}

      {/* ── Sliding sheet ───────────────────────────────────────────── */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('chat.floatingTitle') || 'AI Concierge'}
        data-testid="floating-ai-widget-panel"
        className={`
          fixed z-[80]
          right-0 top-0
          h-full w-full md:w-[480px] lg:w-[520px]
          bg-ivory-50
          border-l border-gold-300/40
          shadow-[-24px_0_60px_rgba(15,23,42,0.35)]
          transform transition-transform duration-300 ease-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          flex flex-col
        `}
      >
        {/* Sheet header — matches the ChatHeader tone but tighter */}
        <header className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b border-gold-300/40 bg-midnight-900 text-gold-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gold-400/15 border border-gold-400/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-gold-300" strokeWidth={1.75} />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-gold-400/90">
                {t('chat.floatingKicker') || 'Live Concierge'}
              </div>
              <div className="font-serif italic text-lg text-ivory-50">
                {t('chat.floatingTitle') || 'Chat with Jarvis'}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={closePanel}
            aria-label={t('common.close') || 'Close'}
            data-testid="floating-ai-widget-close"
            className="w-9 h-9 rounded-full flex items-center justify-center text-ivory-200/80 hover:text-gold-300 hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>
        </header>

        {/* Chat body — mounts AIChat lazily on first open, then stays. */}
        <div className="flex-1 overflow-y-auto bg-ivory-50">
          {everOpened && (
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-2 border-gold-300 border-t-midnight-900 rounded-full animate-spin" />
                </div>
              }
            >
              <div className="py-3">
                <AIChat onNavigate={handleChatNavigate} pageContext={activeSection || 'home'} />
              </div>
            </Suspense>
          )}
        </div>
      </aside>
    </>
  );
}
